import { cartLineItemsOf } from './cartLines'
import { orderDateOf, orderDateText, orderUnitCount, type ApiDate } from './orderRows'
import { toReorderQuantities } from './reorderLines'
import type { OrderLineInput } from './orderDetail'

export interface LastOrderInput {
  id: string
  createdAt?: ApiDate | string | number | null
  items?: readonly OrderLineInput[] | null
  /** Present instead of `items` on a storefront whose cart is grouped. */
  groups?: readonly { items?: readonly OrderLineInput[] | null }[] | null
}

export interface LastOrderModel {
  id: string
  /**
   * "6 ago 2026", or `null` when the order carries no usable date. `null` is
   * not an em dash: the caller is expected to drop the sentence, not to render
   * a placeholder where a date was promised.
   */
  dateText: string | null
  /** Units, the same way the cart and the history count them. */
  units: number
  /** `sku -> units`, which is what refilling a cart needs. */
  quantities: Record<string, number>
}

/**
 * The last order, reduced to the two things a "repeat it" control can act on:
 * when it happened, and what was in it.
 *
 * Returns `null` for everything that cannot honestly be offered — no order, or
 * an order whose lines carry no sku. A shopper with no purchase history gets
 * no control and no sentence rather than an empty one, which is the same rule
 * the suggested basket and the promotion reels follow.
 *
 * A missing or unparseable date is *not* one of those cases. The lines are
 * still real and still repeatable; only the sentence about when has to go, so
 * `dateText` goes to `null` and the order survives.
 */
export const toLastOrderModel = (
  order: LastOrderInput | null | undefined,
  locale: string,
): LastOrderModel | null => {
  if (!order) return null

  const lines = cartLineItemsOf<OrderLineInput>(order)
    .map((item) => ({ sku: item?.sku ?? '', quantity: item?.quantity ?? 0 }))
    // A line with no sku names no product, and one asking for zero units asks
    // for nothing — `quantity` is nullable on the schema, so both are shapes
    // the server can really send. Kept, they would reach `prefillCart` as an
    // item of `0`, which is how that mutation is told to *remove* a line.
    .filter((line) => line.sku !== '' && line.quantity > 0)

  if (lines.length === 0) return null

  return {
    id: order.id,
    dateText: orderDateOf(order.createdAt) ? orderDateText(order.createdAt, locale) : null,
    units: orderUnitCount(lines),
    quantities: toReorderQuantities(lines),
  }
}
